/* eslint-disable indent */
import PropTypes from 'prop-types';
import UserProgramsModel from './userProgramsModel';
import UserSettingsModel from './userSettingsModel';

/**
 * @typedef {Object} UserConsentPurpose
 * @property {string} purposeCode Consent purpose code
 * @property {string} displayName Consent display name
 * @property {boolean} isRequired Whether the consent is required
 * @property {boolean} isGranted Whether the user granted the consent
 * @property {string} status Consent status from the backend
 * @property {string} category Consent category
 * @property {string} description Consent description
 * @property {string} grantedDate ISO date when consent was granted
 * @property {string} expirationDate Optional ISO date when an ungranted consent expires
 */

const userConsentPurposePropType = PropTypes.shape({
  purposeCode: PropTypes.string,
  displayName: PropTypes.string,
  isRequired: PropTypes.bool,
  isGranted: PropTypes.bool,
  status: PropTypes.string,
  category: PropTypes.string,
  description: PropTypes.string,
  grantedDate: PropTypes.string,
  expirationDate: PropTypes.string,
});

/**
 * User model
 * @class
 */
class UserModel {
  /**
   *
   * @param {object} dto The data transfer object
   * @returns {UserModel} The newly created user model
   */
  static fromDto(dto) {
    return new UserModel(
      dto.userId,
      dto.emailAddress,
      dto.firstName,
      dto.fullName,
      dto.lastName,
      dto.mostRecentHistory,
      dto.userSettings,
      dto.subscriptionStatus,
      dto.isExamScheduled,
      dto.userProgramsSettings,
      dto.userOrganizationIdentifiers,
      dto.activeProducts,
      dto.programIds,
      dto.surveyStatuses,
      dto.consentPurposes,
    );
  }

  /**
   *
   * @param {string} userId The user id
   * @param {string} emailAddress The email address
   * @param {string} firstName The first name
   * @param {string} lastName The last name
   * @param {string} fullName The full name
   * @param {Object} mostRecentHistory Most recent prep test info
   * @param {UserSettingsModel} userSettings The user settings model
   * @param {Object} subscriptionStatus The subscription status
   * @param {bool} isExamScheduled Is the exam sheduled
   * @param {UserProgramsModel} userPrograms The user program model
   * @param {Object} userOrganizationIdentifiers User organizations, ID and name
   * @param {Object} activeProducts Products the user has access to
   * @param {Object} programIds Array of programIds
   * @param {Object[]} surveyStatuses Array of available surveys
   * @param {UserConsentPurpose[]} consentPurposes Array of consent purposes
   */
  constructor(
    userId,
    emailAddress,
    firstName,
    fullName,
    lastName,
    mostRecentHistory,
    userSettings,
    subscriptionStatus,
    isExamScheduled,
    userPrograms,
    userOrganizationIdentifiers,
    activeProducts,
    programIds,
    surveyStatuses,
    consentPurposes,
  ) {
    this.userId = userId;
    this.emailAddress = emailAddress;
    this.firstName = firstName;
    this.fullName = fullName;
    this.lastName = lastName;
    this.mostRecentHistory = mostRecentHistory;
    this.userSettings = new UserSettingsModel(
      userSettings.isExtraTime,
      userSettings.extraTimeAmount,
      userSettings.minutesPerSection,
      userSettings.isUsingFindTextFeature,
      userSettings.minutesPerAnalysis,
    );
    this.subscriptionStatus = subscriptionStatus;
    this.isExamScheduled = isExamScheduled;
    this.userPrograms = userPrograms
      ? userPrograms.map(
          program =>
            new UserProgramsModel(
              program.programId,
              program.programName,
              program.userSettings,
            ),
        )
      : [];
    this.userOrganizationIdentifiers = userOrganizationIdentifiers
      ? userOrganizationIdentifiers.map(organizationObj => ({
          organization: organizationObj?.organization,
          organizationIdentifier: organizationObj?.organizationIdentifier,
        }))
      : [];
    this.activeProducts = activeProducts;
    this.programIds = programIds;
    this.surveyStatuses = surveyStatuses;
    this.consentPurposes = consentPurposes || [];
  }
}

UserModel.propTypes = {
  userId: PropTypes.string.isRequired,
  emailAddress: PropTypes.string.isRequired,
  firstName: PropTypes.string.isRequired,
  lastName: PropTypes.string.isRequired,
  isExamScheduled: PropTypes.bool.isRequired,
  userSettings: PropTypes.instanceOf(UserSettingsModel),
  userPrograms: PropTypes.arrayOf(UserProgramsModel),
  userOrganizationIdentifiers: PropTypes.array,
  activeProducts: PropTypes.object,
  subscriptionStatus: PropTypes.object,
  consentPurposes: PropTypes.arrayOf(userConsentPurposePropType),
};

export default UserModel;
